import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { useApiClient } from '@eluve/api-client-provider';
import {
  CreateUserInviteRequest,
  createUserInviteRequestSchema,
} from '@eluve/api-contract';
import {
  Button,
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  Icon,
  Input,
  Stack,
  ToggleGroup,
  ToggleGroupItem,
  useToast,
} from '@eluve/components';
import { useTenantId } from '@eluve/frontend-appointment-hooks';
import { useTranslation } from '@eluve/i18n';
import { useAnalytics } from '@eluve/posthog-react';
import { useUserIdFromSession } from '@eluve/session-helpers';
import {
  useIsFeatureFlagEnabled,
  useTenantLocations,
} from '@eluve/smart-blocks';
import { useDialog } from '@eluve/utility-hooks';

const defaultFormValues = {
  email: '',
  role: 'tenant-member' as const,
  firstName: '',
  lastName: '',
  locationId: undefined,
};

type InviteForm = CreateUserInviteRequest;

export const InviteUserModal: FC<{ onSuccess: () => void }> = ({
  onSuccess,
}) => {
  const api = useApiClient();
  const { t } = useTranslation();

  const tenantId = useTenantId();
  const userId = useUserIdFromSession();
  const locationsEnabled = useIsFeatureFlagEnabled('LOCATIONS');
  const { locations } = useTenantLocations({ skip: !locationsEnabled });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isDialogOpen, toggleDialog } = useDialog(false);
  const toast = useToast();

  const analytics = useAnalytics('organization_invites');

  const form = useForm<InviteForm>({
    defaultValues: defaultFormValues,
    resolver: zodResolver(createUserInviteRequestSchema),
  });

  const handleOpenChange = () => {
    toggleDialog();
    form.reset(defaultFormValues);
    setIsSubmitting(false);
  };

  const onSubmit = async (values: InviteForm) => {
    if (!values.email.trim()) {
      return;
    }

    setIsSubmitting(true);

    const response = await api.tenantAdminUsers.createInvite({
      params: {
        tenantId,
      },
      body: {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        locationId: values.locationId,
        role: values.role,
      },
    });

    if (response.status === 200) {
      onSuccess();
    } else {
      toast.error(t('tenantAdmin.users.inviteSendFailed'));
    }

    handleOpenChange();
  };

  useEffect(() => {
    if (isDialogOpen && tenantId && userId) {
      analytics.capture('modal_open', {
        tenantId,
        userId,
      });
    }
  }, [isDialogOpen, tenantId, userId, analytics]);

  return (
    <>
      <Button onClick={toggleDialog}>
        <Icon name="UserRoundPlus" size="auto" />
        {t('tenantAdmin.users.inviteUser')}
      </Button>
      <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
        <Form {...form}>
          <DialogContent size="lg">
            <DialogHeader>
              <DialogTitle>
                {t('tenantAdmin.users.inviteNewUserTitle')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <Stack className="my-4" gap={4}>
                <FormField
                  control={form.control}
                  disabled={isSubmitting}
                  name="role"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>
                        {t('tenantAdmin.users.userRoleLabel')}
                      </FormLabel>
                      <FormControl>
                        <ToggleGroup
                          disabled={isSubmitting}
                          fullWidth
                          onValueChange={(value) => {
                            if (value) {
                              field.onChange(value);
                            }
                          }}
                          type="single"
                          value={field.value}
                          variant="outline"
                        >
                          <ToggleGroupItem value="tenant-admin">
                            {t('tenantAdmin.users.roleAdmin')}
                          </ToggleGroupItem>
                          <ToggleGroupItem value="tenant-member">
                            {t('tenantAdmin.users.roleMember')}
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </FormControl>
                      <FormDescription>
                        {t('tenantAdmin.users.roleDescription')}
                      </FormDescription>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  disabled={isSubmitting}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>{t('tenantAdmin.users.emailLabel')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('tenantAdmin.users.emailPlaceholder')}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <Stack gap={4} direction="horizontal">
                  <FormField
                    control={form.control}
                    disabled={isSubmitting}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel>
                          {t('tenantAdmin.users.firstNameLabel')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t(
                              'tenantAdmin.users.firstNamePlaceholder',
                            )}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    disabled={isSubmitting}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel>
                          {t('tenantAdmin.users.lastNameLabel')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t(
                              'tenantAdmin.users.lastNamePlaceholder',
                            )}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </Stack>
                {locationsEnabled && (
                  <FormField
                    control={form.control}
                    name="locationId"
                    render={({ field }) => {
                      const selectedLocation = locations.find(
                        (location) => location.id === field.value,
                      );
                      return (
                        <FormItem className="w-full">
                          <FormLabel>
                            {t('tenantAdmin.users.locationLabel')}
                          </FormLabel>
                          <FormControl>
                            <Combobox disabled={isSubmitting}>
                              <ComboboxSelectButton
                                className="w-full rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-secondary"
                                dropdownIcon={
                                  <Icon
                                    className="ml-2 opacity-50"
                                    name="ChevronDown"
                                  />
                                }
                              >
                                {selectedLocation?.name ??
                                  t('tenantAdmin.users.selectLocation')}
                              </ComboboxSelectButton>
                              <ComboboxDropdown>
                                {locations.map((location) => (
                                  <ComboboxOption
                                    key={location.id}
                                    onSelect={() => field.onChange(location.id)}
                                  >
                                    <ComboboxSelectCheck
                                      className="mr-2"
                                      selected={
                                        selectedLocation?.id === location.id
                                      }
                                    />
                                    {location.name}
                                  </ComboboxOption>
                                ))}
                              </ComboboxDropdown>
                            </Combobox>
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />
                )}
              </Stack>
              <DialogFooter className="mt-8">
                <Button loading={isSubmitting} type="submit">
                  {t('tenantAdmin.users.sendInvitation')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Form>
      </Dialog>
    </>
  );
};
